import { Button } from '@factorialco/deprecated-design-system'
import { some } from 'lodash'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'

import type { Expensable } from 'modules/expenses/components/expenses/DataTable/types'
import { ActionFn } from 'modules/expenses/components/expenses/DataTable/useActions'
import { useAvailableActions } from 'modules/expenses/lib/expensables/availableActions'
import {
  everyReimbursableCurrencyAllowed,
  showNotAllowedCurrenciesMessage,
} from 'modules/expenses/lib/expensables/expensable'

import { Options } from './Options'

interface ExpensableActionsProps {
  expensable: Expensable
  onApprove: ActionFn
  onReject: ActionFn
  onRestartReview: ActionFn
  onAddToPayroll: ActionFn
  onAddToPayment: ActionFn
  onRemoveFromPayroll: ActionFn
  onSetToPaid: ActionFn
  onRevertPaid: ActionFn
  onAddToGroup: ActionFn
  onRemoveFromGroup: ActionFn
  canRestartApprovalFlow: boolean
  canSetExpensableToPaid: boolean
}

export const Actions = observer(
  ({
    expensable,
    onApprove,
    onReject,
    onRestartReview,
    onAddToPayroll,
    onAddToPayment,
    onRemoveFromPayroll,
    onSetToPaid,
    onRevertPaid,
    onAddToGroup,
    onRemoveFromGroup,
    canRestartApprovalFlow,
    canSetExpensableToPaid,
  }: ExpensableActionsProps) => {
    const availableActions = useAvailableActions(
      [expensable],
      canRestartApprovalFlow,
      canSetExpensableToPaid
    )
    const withExpensable = (action: ActionFn) => () => action([expensable])

    if (!some(availableActions)) return null

    return (
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button.Decorative type="secondary" iconName="ellipsis" />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="start">
          <Options
            {...availableActions}
            onApprove={withExpensable(onApprove)}
            onReject={withExpensable(onReject)}
            onRestartReview={withExpensable(onRestartReview)}
            onSetToPaid={withExpensable(onSetToPaid)}
            onAddToPayment={withExpensable(onAddToPayment)}
            onRevertPaid={withExpensable(onRevertPaid)}
            onRemoveFromPayroll={withExpensable(onRemoveFromPayroll)}
            onAddToGroup={withExpensable(onAddToGroup)}
            onRemoveFromGroup={withExpensable(onRemoveFromGroup)}
            onAddToPayroll={() => {
              if (!everyReimbursableCurrencyAllowed([expensable])) {
                return showNotAllowedCurrenciesMessage()
              }

              withExpensable(onAddToPayroll)()
            }}
          />
        </PopoverMenu.Content>
      </PopoverMenu>
    )
  }
)
